import React, { ComponentProps } from "react";
import { Button, Container, Spinner } from "react-bootstrap";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import useCompetition from "../hooks/useCompetition";
import { logout } from "../services/auth";
import locationToString from "../utils/locationToString";

const VenueLogoDiv = styled.div<{ url?: string }>`
  background-color: white;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0px 7px 12px 0px #00000026;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #00000026;

  img {
    object-fit: cover;
    width: 48px;
    height: 48px;
  }
`;

const VenueLogo = ({ url, ...props }: ComponentProps<typeof VenueLogoDiv>) => (
  <VenueLogoDiv {...props}>
    {url ? <img src={url} /> : <Spinner animation="border" />}
  </VenueLogoDiv>
);

const CompetitionHeaderLayout = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { competitionUUID } = useParams();
  const { competition, venue } = useCompetition(competitionUUID);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const atOnboardingStart = pathname.match(
    /^\/[^/]+\/(enter|judge|judge\/onboarding)$/
  );
  const afterAuthWall = pathname.match(
    /^\/[^/]+\/(enter\/confirm|enter\/competitors|judge\/onboarding\/kids)$/
  );
  const canGoBack = !atOnboardingStart && !afterAuthWall;
  const showLogout = afterAuthWall;

  const onLogout = () => {
    if (pathname.match(/^\/[^/]+\/enter$/)) {
      logout(`/${competitionUUID}/enter`);
    } else {
      logout(`/${competitionUUID}/judge/onboarding`);
    }
  };

  return (
    <div>
      <div style={{ minHeight: "calc(100vh - 100px)" }}>
        <div style={{ height: 40 }}>
          {canGoBack ? (
            <Button variant="" onClick={() => navigate(-1)}>
              ← Back
            </Button>
          ) : null}
          {showLogout ? (
            <Button variant="" onClick={onLogout}>
              × Logout
            </Button>
          ) : null}
        </div>
        <div className="d-flex align-items-center flex-column mb-4">
          <VenueLogo className="mb-3" url={venue?.logoUrl ?? undefined} />
          <div className="text-center">
            <h5>{competition?.name || <br />}</h5>
            <div>{venue?.name || <br />}</div>
            <small>
              {venue ? (
                locationToString({
                  city: venue.city,
                  state: venue.state ?? undefined,
                  country: venue.country,
                })
              ) : (
                <br />
              )}
            </small>
          </div>
        </div>
        <Container>
          <Outlet />
          {children}
        </Container>
      </div>
      <svg
        width="150px"
        height="70px"
        viewBox="0 0 119 57"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="d-block m-auto"
      >
        <path
          d="M8.58982 42.0176L7.21289 44.6391C7.21289 44.6391 13.6025 45.9101 13.6025 45.926L14.6545 42.812C14.4225 42.7643 8.58982 42.0176 8.58982 42.0176Z"
          fill="#21E700"
        />
        <mask
          id="mask0"
          mask-type="alpha"
          maskUnits="userSpaceOnUse"
          x="0"
          y="23"
          width="22"
          height="34"
        >
          <path
            d="M0.142578 23.0889H21.7867V56.9141H0.142578V23.0889Z"
            fill="black"
          />
        </mask>
        <g mask="url(#mask0)">
          <path
            d="M19.6512 29.062L11.3277 23.9461C9.6259 22.8975 7.55277 22.818 5.78905 23.7236C4.00987 24.6292 2.81859 26.361 2.60199 28.3947L0.173018 51.3844C0.00283493 53.0049 0.575268 54.5461 1.75108 55.6264C2.74123 56.532 4.00987 56.9769 5.29397 56.8975C5.54151 56.8816 5.78905 56.8498 6.03659 56.7863L11.9311 55.4676C13.8959 55.0227 15.4585 53.6087 16.1393 51.6704L17.609 47.4283C17.9339 46.4909 17.8411 45.4582 17.3305 44.5844C16.9438 43.9171 16.3713 43.4246 15.6751 43.1545L13.1533 50.59C12.8439 51.4638 12.1477 52.1152 11.2504 52.3059L5.35586 53.6246C4.81437 53.7517 4.25741 53.5928 3.85516 53.2115C3.43743 52.8302 3.23631 52.29 3.29819 51.718L5.74264 28.7442C5.83547 27.8068 6.36149 27.0442 7.18146 26.6152C8.00143 26.2021 8.91423 26.2339 9.70326 26.7106L18.0267 31.8423C18.4909 32.1283 18.7229 32.6844 18.5992 33.2246L18.4909 36.704L20.7342 38.0227L21.6625 33.9713C22.1111 32.0489 21.3066 30.0788 19.6512 29.062Z"
            fill="black"
          />
        </g>
        <path
          d="M41.4637 47.3592H41.3709C41.1698 48.7891 39.9011 49.8694 37.8125 49.8694C34.6255 49.8694 32.1191 47.3274 32.1191 42.5452C32.1191 37.7311 34.6564 35.0938 38.4159 35.0938C40.8913 35.0938 42.7324 36.2218 43.7689 38.4143L41.2935 39.8601C40.8758 38.748 40.0249 37.8582 38.4004 37.8582C36.5284 37.8582 35.3062 38.9704 35.3062 41.3377V43.6255C35.3062 45.9928 36.5284 47.105 38.4004 47.105C39.9166 47.105 41.1234 46.3741 41.1234 44.976V44.261H38.5397V41.6554H44.0165V49.6311H41.4637V47.3592Z"
          fill="black"
        />
        <path
          d="M45.9971 49.6318V38.8916H48.9521V41.2271H49.0449C49.277 40.0355 50.0815 38.8916 51.7214 38.8916H52.2319V41.7196H51.4893C49.772 41.7196 48.9366 42.1009 48.9366 43.3561V49.6318H45.9971Z"
          fill="black"
        />
        <path
          d="M52.9902 36.2537V35.8089C52.9902 34.9509 53.5008 34.3154 54.7075 34.3154C55.8988 34.3154 56.4248 34.9509 56.4248 35.8089V36.2537C56.4248 37.1117 55.9143 37.7472 54.7075 37.7472C53.5008 37.7472 52.9902 37.1117 52.9902 36.2537ZM53.2223 38.8911H56.1773V49.6313H53.2223V38.8911Z"
          fill="black"
        />
        <path
          d="M58.0947 38.8909H61.0497V40.6863H61.1425C61.4365 39.4947 62.5968 38.6367 63.9583 38.6367C66.6039 38.6367 67.9808 40.6068 67.9808 44.2451C67.9808 47.8834 66.6039 49.8694 63.9583 49.8694C62.5968 49.8694 61.452 48.9956 61.1425 47.8199H61.0497V53.7143H58.0947V38.8909ZM64.9175 45.1984V43.3395C64.9175 41.9891 64.0975 41.1152 62.9836 41.1152C61.8697 41.1152 61.0497 41.7031 61.0497 42.6881V45.8657C61.0497 46.8507 61.8697 47.4386 62.9836 47.4386C64.0975 47.4227 64.9175 46.5489 64.9175 45.1984Z"
          fill="black"
        />
        <path
          d="M72.9476 49.6311C70.9209 49.6311 69.8379 48.5507 69.8379 46.5012V41.2423H68.3682V38.8909H69.1108C69.9153 38.8909 70.1473 38.4778 70.1473 37.7469V35.9834H72.7929V38.8909H74.8506V41.2423H72.7774V47.2797H74.6959V49.6311H72.9476Z"
          fill="black"
        />
        <path
          d="M75.3613 44.2451C75.3613 40.7816 77.1869 38.6367 80.2657 38.6367C83.3445 38.6367 85.1701 40.7816 85.1701 44.2451C85.1701 47.7087 83.3445 49.8694 80.2657 49.8694C77.1869 49.8694 75.3613 47.7087 75.3613 44.2451ZM82.0913 45.3573V43.1648C82.0913 41.8143 81.4106 41.0358 80.2657 41.0358C79.1208 41.0358 78.4401 41.8143 78.4401 43.1648V45.3573C78.4401 46.7077 79.1208 47.4862 80.2657 47.4862C81.4106 47.4862 82.0913 46.7077 82.0913 45.3573Z"
          fill="black"
        />
        <path
          d="M86.4229 49.6308V38.8907H89.3779V40.7178H89.5016C89.8729 39.5897 90.7548 38.6523 92.4102 38.6523C94.5452 38.6523 95.6746 40.2094 95.6746 42.9103V49.6308H92.7196V43.1486C92.7196 41.7822 92.3483 41.0991 91.2653 41.0991C90.3061 41.0991 89.3779 41.6075 89.3779 42.672V49.6308H86.4229Z"
          fill="black"
        />
        <path
          d="M97.2529 36.2537V35.8089C97.2529 34.9509 97.7635 34.3154 98.9702 34.3154C100.177 34.3154 100.688 34.9509 100.688 35.8089V36.2537C100.688 37.1117 100.177 37.7472 98.9702 37.7472C97.7635 37.7472 97.2529 37.1117 97.2529 36.2537ZM97.485 38.8911H100.44V49.6313H97.485V38.8911Z"
          fill="black"
        />
        <path
          d="M106.025 49.6311C103.998 49.6311 102.915 48.5507 102.915 46.5012V41.2423H101.445V38.8909H102.188C102.992 38.8909 103.224 38.4778 103.224 37.7469V35.9834H105.87V38.8909H107.943V41.2423H105.87V47.2797H107.788V49.6311H106.025Z"
          fill="black"
        />
        <path
          d="M108.438 44.2451C108.438 40.8451 110.187 38.6367 113.343 38.6367C116.808 38.6367 118.201 41.2105 118.201 44.118V45.0236H111.486V45.1825C111.486 46.5965 112.229 47.4862 113.792 47.4862C115.029 47.4862 115.71 46.8984 116.36 46.1993L117.83 48.0741C116.886 49.1862 115.354 49.8535 113.451 49.8535C110.249 49.8694 108.438 47.6928 108.438 44.2451ZM111.486 43.0853V43.2442H115.153V43.0535C115.153 41.7507 114.581 40.861 113.405 40.861C112.229 40.8769 111.486 41.7507 111.486 43.0853Z"
          fill="black"
        />
        <path
          opacity="0.5"
          d="M34.7738 23V18.828H37.4898C39.2398 18.828 40.2198 17.75 40.2198 16.028C40.2198 14.306 39.2398 13.228 37.4898 13.228H33.5978V23H34.7738ZM34.7738 14.264H37.4898C38.4138 14.264 38.9598 14.768 38.9598 15.664V16.392C38.9598 17.288 38.4138 17.792 37.4898 17.792H34.7738V14.264ZM44.1926 23.168C46.1246 23.168 47.4546 21.684 47.4546 19.388C47.4546 17.092 46.1246 15.608 44.1926 15.608C42.2606 15.608 40.9306 17.092 40.9306 19.388C40.9306 21.684 42.2606 23.168 44.1926 23.168ZM44.1926 22.174C42.9886 22.174 42.1346 21.432 42.1346 19.906V18.87C42.1346 17.344 42.9886 16.602 44.1926 16.602C45.3966 16.602 46.2506 17.344 46.2506 18.87V19.906C46.2506 21.432 45.3966 22.174 44.1926 22.174ZM50.1642 23H51.5222L52.5302 19.528L53.1462 17.33H53.1742L53.8042 19.528L54.8122 23H56.1982L58.1162 15.776H57.0662L56.2822 18.898L55.5402 22.062H55.5122L54.6162 18.898L53.6782 15.776H52.6842L51.7742 18.898L50.8922 22.062H50.8642L50.0942 18.898L49.3242 15.776H48.2322L50.1642 23ZM62.142 23.168C63.5 23.168 64.536 22.496 64.998 21.516L64.2 20.942C63.822 21.74 63.122 22.188 62.212 22.188C60.868 22.188 60.07 21.25 60.07 20.004V19.668H65.264V19.136C65.264 17.092 64.074 15.608 62.142 15.608C60.182 15.608 58.894 17.092 58.894 19.388C58.894 21.684 60.182 23.168 62.142 23.168ZM62.142 16.546C63.29 16.546 64.06 17.4 64.06 18.674V18.828H60.07V18.73C60.07 17.47 60.924 16.546 62.142 16.546ZM67.9896 23V18.1C67.9896 17.414 68.7176 16.896 69.9216 16.896H70.5796V15.776H70.1456C68.9696 15.776 68.3116 16.42 68.0596 17.106H67.9896V15.776H66.8696V23H67.9896ZM74.4178 23.168C75.7758 23.168 76.8118 22.496 77.2738 21.516L76.4758 20.942C76.0978 21.74 75.3978 22.188 74.4878 22.188C73.1438 22.188 72.3458 21.25 72.3458 20.004V19.668H77.5398V19.136C77.5398 17.092 76.3498 15.608 74.4178 15.608C72.4578 15.608 71.1698 17.092 71.1698 19.388C71.1698 21.684 72.4578 23.168 74.4178 23.168ZM74.4178 16.546C75.5658 16.546 76.3358 17.4 76.3358 18.674V18.828H72.3458V18.73C72.3458 17.47 73.1998 16.546 74.4178 16.546ZM83.7654 23H84.8854V12.64H83.7654V16.952H83.7094C83.3314 16.042 82.5894 15.608 81.5954 15.608C79.7894 15.608 78.6554 17.078 78.6554 19.388C78.6554 21.698 79.7894 23.168 81.5954 23.168C82.5894 23.168 83.2754 22.72 83.7094 21.824H83.7654V23ZM81.9034 22.16C80.6434 22.16 79.8594 21.292 79.8594 20.004V18.772C79.8594 17.484 80.6434 16.616 81.9034 16.616C82.9254 16.616 83.7654 17.204 83.7654 18.002V20.69C83.7654 21.628 82.9254 22.16 81.9034 22.16ZM90.0951 23H91.2151V21.824H91.2711C91.7051 22.72 92.3911 23.168 93.3851 23.168C95.1911 23.168 96.3251 21.698 96.3251 19.388C96.3251 17.078 95.1911 15.608 93.3851 15.608C92.3911 15.608 91.6491 16.042 91.2711 16.952H91.2151V12.64H90.0951V23ZM93.0771 22.16C92.0551 22.16 91.2151 21.628 91.2151 20.69V18.002C91.2151 17.204 92.0551 16.616 93.0771 16.616C94.3371 16.616 95.1211 17.484 95.1211 18.772V20.004C95.1211 21.292 94.3371 22.16 93.0771 22.16ZM100.61 20.69L100.218 21.866H100.148L99.8118 20.69L98.0758 15.776H96.9558L99.6578 23.28L99.1118 24.82H97.9778V25.8H98.5518C99.6158 25.8 99.9098 25.548 100.232 24.68L103.466 15.776H102.36L100.61 20.69Z"
          fill="black"
        />
      </svg>
    </div>
  );
};

export default CompetitionHeaderLayout;
